import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, LangToggle } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

export const AuthShell = ({ children }) => {
  const { L, brand } = useApp();
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-between p-6 sm:p-10">
        <div className="flex items-center justify-between"><Logo /><LangToggle /></div>
        <div className="mx-auto w-full max-w-sm py-12">{children}</div>
        <p className="text-xs text-slate-400">© {brand.company_name} · <Link to="/privacy" className="underline">{L("Privasi", "Privacy")}</Link> · <Link to="/terms" className="underline">{L("Terma", "Terms")}</Link></p>
      </div>
      <div className="hidden flex-col justify-end bg-slate-900 p-12 text-white lg:flex">
        <div className="mb-10 space-y-3">
          {["[00:42] Pembicara 1: Kita setuju bajet dinaikkan 10%.", "[01:15] Pembicara 2: Saya akan siapkan laporan Jumaat ini."].map((l) => (
            <p key={l} className="rounded-xl bg-white/5 p-3 font-mono text-sm text-slate-300">{l}</p>
          ))}
          <p className="rounded-xl bg-violet-600 p-3 text-sm font-semibold">✓ {L("Keputusan: Bajet dinaikkan 10%", "Decision: Budget increased by 10%")}</p>
        </div>
        <p className="max-w-md font-heading text-2xl font-bold leading-snug">{L("Fokus pada mesyuarat. Biar AI uruskan nota.", "Focus on the meeting. Let AI handle the notes.")}</p>
      </div>
    </div>
  );
};

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { L } = useApp();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const qs = new URLSearchParams(window.location.search);
  const invited = !isLogin && (qs.get("invite") || sessionStorage.getItem("minitai-join"));
  const [form, setForm] = useState({ name: "", email: qs.get("email") || "", password: "", phone: "", department: "", position: "", organisation: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to={user.onboarded ? "/app" : "/onboarding"} replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const me = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password, invited ? { phone: form.phone, department: form.department, position: form.position, organisation: form.organisation } : { phone: form.phone, department: form.department });
      const join = sessionStorage.getItem("minitai-join");
      navigate(join ? `/join/${join}` : me?.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-5" data-testid={`${mode}-form`}>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{isLogin ? L("Log masuk", "Log in") : L("Cipta akaun", "Create account")}</h1>
          <p className="mt-2 text-sm text-slate-500">{isLogin ? L("Selamat kembali.", "Welcome back.") : L("Percuma untuk bermula — tiada kad kredit.", "Free to start — no credit card.")}</p>
        </div>
        {!isLogin && <div className="space-y-2"><Label htmlFor="name">{L("Nama penuh", "Full name")}</Label><Input id="name" required value={form.name} onChange={set("name")} className="h-11 rounded-xl" data-testid="register-name-input" /></div>}
        {invited && (
          <div className="grid gap-4 rounded-2xl border border-violet-200 bg-violet-50/50 p-4 sm:grid-cols-2" data-testid="invite-profile-fields">
            <p className="text-xs font-semibold text-violet-800 sm:col-span-2">{L("Maklumat ahli pasukan (dipaparkan kepada pemilik workspace)", "Team member details (shown to the workspace owner)")}</p>
            {[["phone", "No. telefon", "Phone number", "tel"], ["position", "Jawatan", "Position", "text"], ["department", "Jabatan / Unit", "Department / Unit", "text"], ["organisation", "Organisasi", "Organisation", "text"]].map(([k, ms, en, type]) => (
              <div key={k} className="space-y-1.5"><Label htmlFor={k}>{L(ms, en)}</Label><Input id={k} type={type} required={k !== "organisation"} value={form[k]} onChange={set(k)} maxLength={120} className="h-10 rounded-xl bg-white" data-testid={`register-${k}-input`} /></div>
            ))}
          </div>
        )}
        {!isLogin && !invited && (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="register-optional-fields">
            {[["phone", "No. telefon (pilihan)", "Phone (optional)", "tel"], ["department", "Jabatan (pilihan)", "Department (optional)", "text"]].map(([k, ms, en, type]) => (
              <div key={k} className="space-y-1.5"><Label htmlFor={k}>{L(ms, en)}</Label><Input id={k} type={type} value={form[k]} onChange={set(k)} maxLength={120} className="h-11 rounded-xl" data-testid={`register-${k}-input`} /></div>
            ))}
          </div>
        )}
        <div className="space-y-2"><Label htmlFor="email">{L("Emel", "Email")}</Label><Input id="email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} className="h-11 rounded-xl" data-testid={`${mode}-email-input`} /></div>
        <div className="space-y-2">
          <div className="flex justify-between"><Label htmlFor="password">{L("Kata laluan", "Password")}</Label>{isLogin && <Link to="/forgot-password" className="text-xs font-semibold text-violet-700" data-testid="forgot-password-link">{L("Lupa kata laluan?", "Forgot password?")}</Link>}</div>
          <Input id="password" type="password" autoComplete={isLogin ? "current-password" : "new-password"} required minLength={isLogin ? 1 : 8} value={form.password} onChange={set("password")} className="h-11 rounded-xl" data-testid={`${mode}-password-input`} />
          {!isLogin && <p className="text-xs text-slate-500">{L("Sekurang-kurangnya 8 aksara.", "At least 8 characters.")}</p>}
        </div>
        {error && <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid={`${mode}-error`}>{error}</p>}
        <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-violet-600 hover:bg-violet-700" data-testid={`${mode}-submit-btn`}>
          {loading && <Loader2 className="mr-2 animate-spin" size={16} />}{isLogin ? L("Log masuk", "Log in") : L("Daftar", "Sign up")}
        </Button>
        <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />{L("atau", "or")}<span className="h-px flex-1 bg-slate-200" /></div>
        <GoogleButton label={isLogin ? L("Log masuk dengan Google", "Log in with Google") : L("Daftar dengan Google", "Sign up with Google")} />
        <p className="text-center text-sm text-slate-500">
          {isLogin ? L("Belum ada akaun? ", "No account? ") : L("Sudah ada akaun? ", "Have an account? ")}
          <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-violet-700" data-testid={`${mode}-switch-link`}>{isLogin ? L("Daftar", "Sign up") : L("Log masuk", "Log in")}</Link>
        </p>
      </form>
    </AuthShell>
  );
}
